import { Check, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang, useT } from "@/content/landing";
import { useSiteSettings, freeSlotsLine, fillTemplate, eur } from "@/lib/siteSettings";
import { Reveal } from "./Reveal";

const cardBase = "flex h-full flex-col rounded-3xl border border-black/5 bg-[#FAFBFC] p-7";
const outlineBtn = "mt-7 inline-block rounded-full border border-[#0F172A] px-6 py-3 text-center text-sm font-semibold text-[#0F172A] transition-colors hover:bg-[#0F172A] hover:text-white focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2 focus-visible:outline-none";
const solidBtn = "mt-7 inline-block rounded-full px-6 py-3 text-center text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:hover:translate-y-0";

function Items({ items }) {
  return <ul className="mt-6 flex-1 space-y-3 text-sm text-[#64748B]">
      {items.map(item => <li key={item} className="flex gap-3">
          <Check size={17} aria-hidden style={{ color: "#22C55E" }} className="mt-0.5 shrink-0" />
          {item}
        </li>)}
    </ul>;
}

// Prices come from GET /api/site-settings; the defaults (39/149) render first,
// so nothing jumps when the answer arrives. While SALES.paymentLive is false
// every CTA leads into the existing request flow (/analyse) — no checkout here.
export function Pricing() {
  const t = useT();
  const { lang } = useLang();
  const s = useSiteSettings();
  const sv = t.services;
  const slots = freeSlotsLine(sv.reportSlots, s);
  const vars = { days: s.consulting_days, max: s.max_tickets, hours: s.response_hours };
  return <section id="preise" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <h2 className="text-2xl font-bold text-[#0F172A] sm:text-3xl">{t.pricing.title}</h2>
          <p className="mt-4 text-base text-[#64748B]">{t.pricing.lead}</p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <Reveal>
            <div className={cardBase}>
              <h3 className="text-lg font-semibold text-[#0F172A]">{sv.freeName}</h3>
              <p className="mt-2 text-3xl font-bold text-[#0F172A]">{t.pricing.free.price}</p>
              <Items items={t.pricing.free.items} />
              <Link to="/analyse" className={outlineBtn}>{t.pricing.free.cta}</Link>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="relative flex h-full flex-col rounded-3xl p-[2px]" style={{ backgroundImage: "linear-gradient(135deg,#3B82F6,#60A5FA,#4ADE80)" }}>
              <div className="flex h-full flex-col rounded-[calc(1.5rem-1px)] bg-white p-7">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-[#0F172A]">{t.pricing.paid.name}</h3>
                  <Lock size={16} aria-hidden style={{ color: "#94A3B8" }} />
                </div>
                <p className="mt-2 flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[#0F172A]">{eur(s.report_price_eur, lang)}</span>
                  <span className="text-sm text-[#94A3B8]">{sv.reportPriceNote}</span>
                </p>
                {slots && <p data-testid="free-slots" className="mt-3 rounded-xl bg-[#EFF6FF] px-3 py-2 text-xs font-medium text-[#1D4ED8]">{slots}</p>}
                <Items items={t.pricing.paid.items} />
                <Link to="/analyse" className={solidBtn} style={{ backgroundColor: "#0F172A" }}>{sv.reportCta}</Link>
                <p className="mt-3 text-xs text-[#94A3B8]">{t.pricing.paid.note}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <div className={cardBase}>
              <h3 className="text-lg font-semibold text-[#0F172A]">{sv.consultingName}</h3>
              <p className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-[#0F172A]">{eur(s.consulting_price_eur, lang)}</span>
                <span className="text-sm text-[#94A3B8]">{fillTemplate(sv.consultingPriceNote, vars)}</span>
              </p>
              <Items items={sv.consultingItems.map(i => fillTemplate(i, vars))} />
              <Link to="/analyse" className={outlineBtn}>{sv.consultingCta}</Link>
              <p className="mt-3 text-xs text-[#94A3B8]">{sv.consultingNote}</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={100}>
          <div className="mt-6 flex flex-col gap-5 rounded-3xl bg-[#0F172A] p-7 text-white sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <h3 className="text-lg font-semibold">{sv.bandTitle}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/80">{sv.bandBody}</p>
              <p className="mt-2 text-xs text-white/60">{sv.bandNote}</p>
            </div>
            <Link to="/angebot" className="shrink-0 rounded-full bg-white px-6 py-3 text-center text-sm font-semibold text-[#0F172A] transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F172A] focus-visible:outline-none motion-reduce:hover:translate-y-0">
              {sv.bandCta}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>;
}
